import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { AlchamisMark } from "@/components/AlchamisMark";
import { AI_SERVICES } from "@/data/content";

export default function AIServices() {
  return (
    <div data-testid="ai-services-page" className="pt-32 pb-24">
      <Seo
        title="AI Services"
        description="Ten AI offerings — from readiness diagnostics to industry-specific AI solutions — built with institutional discipline."
        path="/ai-services"
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <span className="overline">AI Practice</span>
            <h1 className="font-display text-5xl sm:text-7xl text-[#001B54] mt-6 leading-[1.02]">
              Production-grade AI,
              <span className="block italic gold-text">governed like a financial system.</span>
            </h1>
            <p className="text-[#3F4A6B] mt-8 text-base sm:text-lg max-w-2xl leading-relaxed">
              The AlchamisGroup AI practice operates at the intersection of advisory, engineering, and
              governance. Ten offerings, each designed for boards and executives who require AI to be
              defensible — not merely deployed.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8">
              <div className="overline mb-4">Foundational Stack</div>
              <ul className="space-y-3 text-sm text-[#0A1228]">
                <li><span className="text-gold">·</span> Claude Sonnet 4.5, GPT, Gemini</li>
                <li><span className="text-gold">·</span> Retrieval &amp; knowledge graphs</li>
                <li><span className="text-gold">·</span> Agent orchestration</li>
                <li><span className="text-gold">·</span> Observability &amp; evaluation</li>
                <li><span className="text-gold">·</span> Governance &amp; risk telemetry</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-20 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#001B54]/8">
          {AI_SERVICES.map((s, i) => {
            return (
              <div
                key={s.slug}
                data-testid={`ai-service-${s.slug}`}
                className="bg-white p-8 lg:p-10 hover:bg-[#FFFCF4] transition-colors duration-500"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 border border-gold/50 flex items-center justify-center bg-[#001B54]">
                    <AlchamisMark size={22} />
                  </div>
                  <div className="number-badge">AI · {String(i + 1).padStart(2, "0")}</div>
                </div>
                <h3 className="font-display text-2xl text-[#001B54] mt-6 leading-tight">{s.name}</h3>
                <p className="text-[#3F4A6B] text-sm leading-relaxed mt-3">{s.summary}</p>
                <div className="hairline my-6" />
                <div className="overline mb-2">Pillars</div>
                <ul className="space-y-1.5 mb-4">
                  {s.pillars.slice(0, 5).map((p) => (
                    <li key={p} className="text-xs text-[#3F4A6B] flex items-center gap-2">
                      <span className="w-1 h-1 bg-gold/70" /> {p}
                    </li>
                  ))}
                </ul>
                <div className="overline mb-2">Outcomes</div>
                <ul className="space-y-1.5">
                  {s.outcomes.slice(0, 3).map((o) => (
                    <li key={o} className="text-xs text-[#3F4A6B] flex items-center gap-2">
                      <ArrowUpRight size={12} className="text-gold" /> {o}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-24 max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] leading-tight">
          Begin with the AI Diagnostic.
        </h2>
        <p className="text-[#3F4A6B] mt-6 max-w-xl mx-auto">
          Our AI Business Advisor produces a McKinsey-grade executive memo in minutes — calibrated
          across maturity, transformation, and AI readiness.
        </p>
        <div className="mt-8 flex justify-center gap-4 flex-wrap">
          <Link to="/ai-advisor" className="luxury-button" data-testid="ai-services-cta-advisor">Begin Diagnostic <ArrowRight size={14} /></Link>
          <Link to="/contact" className="luxury-button-ghost">Speak to AI Partner</Link>
        </div>
      </section>
    </div>
  );
}
